import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { dateLocale } from '@/lib/locale';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatUZS } from '@/lib/format';
import { PRODUCT_FIELDS, PRODUCT_FIELD_LABELS } from '@/lib/product-schema';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useAuth } from '@/context/AuthContext';
import { RefundDialog } from '@/components/sales/RefundDialog';
import { Undo2, Printer } from 'lucide-react';
import { printReceipt } from '@/lib/receipt-print';
import { useMerchant } from '@/hooks/api/merchant';

type Sale = components['schemas']['SaleResource'];
type SaleItem = components['schemas']['SaleItemResource'];
type Refund = components['schemas']['SaleRefundResource'];

interface Props {
  sale: Sale | null;
  onClose: () => void;
}


const paymentLabel = (method: string, isMurabaha?: boolean) => {
  if (isMurabaha || method === 'murabaha') return i18n.t('misc_common.pay_murabaha');
  if (method === 'cash') return i18n.t('misc_common.pay_cash');
  if (method === 'card') return i18n.t('misc_common.pay_card');
  if (method === 'hisob_wallet') return i18n.t('misc_common.pay_wallet');
  return method || '—';
};

const statusLabel = (status: string) => {
  if (status === 'completed') return i18n.t('misc_sale.st_completed');
  if (status === 'refunded') return i18n.t('misc_sale.st_refunded');
  if (status === 'voided') return i18n.t('misc_sale.st_voided');
  return status || '—';
};

/** Kartochkada ko'rsatiladigan maydonlar — nom va narx alohida chiqadi */
const DETAIL_FIELDS = PRODUCT_FIELDS.filter((f) => f !== 'sale_price' && f !== 'name');

/** Sotuv qatori (nom/birlik/MXIK) sotuv paytidagi nusxa, qolganlari — katalogdagi joriy tovar (Lovable kabi) */
function fieldValue(item: SaleItem, key: string): string {
  const p = (item.product ?? {}) as Record<string, unknown>;
  switch (key) {
    case 'name':
      return item.product_name || '—';
    case 'mhik':
      return item.mhik || (p.mhik as string) || '—';
    case 'unit':
      return item.unit || (p.unit as string) || '—';
    case 'is_murabaha_eligible':
      return p.is_murabaha_eligible === undefined || p.is_murabaha_eligible === null
        ? '—'
        : p.is_murabaha_eligible
        ? i18n.t('misc_common.yes')
        : i18n.t('misc_common.no');
    default:
      return (p[key] as string) || '—';
  }
}

const Info = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="space-y-1">
    <p className="text-sm text-muted-foreground">{label}</p>
    <div className="text-base text-foreground font-medium break-words">{value ?? '—'}</div>
  </div>
);

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="space-y-0.5 min-w-0">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-sm text-foreground font-medium break-words">{value}</p>
  </div>
);

export function SaleDetailDialog({ sale, onClose }: Props) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const [refundOpen, setRefundOpen] = useState(false);
  const { data: merchant } = useMerchant();

  const { data: refunds = [], refetch: refetchRefunds } = useQuery({
    queryKey: ['/sales', sale?.id, '/refunds'],
    queryFn: async (): Promise<Refund[]> => {
      const { data, error } = await apiClient.GET('/sales/{sale}/refunds', {
        params: { path: { sale: sale!.id } },
      });
      if (error) throw error;
      return data?.data ?? [];
    },
    enabled: !!sale?.id,
  });

  if (!sale) return null;

  const items = sale.items || [];
  const payments = sale.payments || [];
  const customer = sale.customer ?? null;
  const murabahaCase = sale.murabaha_case ?? null;
  const itemsTotal = items.reduce((s, i) => s + Number(i.total || 0), 0);
  const refundedTotal = refunds.reduce((s, r) => s + Number(r.amount || 0), 0);
  const canRefund =
    hasRole('owner', 'manager') && sale.status === 'completed' && !sale.is_murabaha;


  return (
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-none w-full h-[100dvh] max-h-[100dvh] rounded-none p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
          <DialogTitle className="text-lg">
            {t('misc_sale.title')} · <span className="font-mono">{sale.id}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl px-4 md:px-6 py-6 space-y-6">
            {/* Sarlavha ma'lumotlari */}
            <section className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Badge variant="secondary" className="text-xs">{statusLabel(sale.status)}</Badge>
                <Badge
                  variant={sale.is_murabaha ? 'outline' : 'secondary'}
                  className={`text-xs ${sale.is_murabaha ? 'border-orange-500/50 text-orange-600 bg-orange-500/10' : ''}`}
                >
                  {paymentLabel(sale.payment_method, sale.is_murabaha)}
                </Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                <Info
                  label={t('misc_sale.sale_id')}
                  value={<span className="font-mono text-sm break-all">{sale.id}</span>}
                />
                <Info label={t('misc_sale.date')} value={new Date(sale.created_at).toLocaleString(dateLocale())} />
                <Info label={t('misc_sale.branch')} value={sale.branch?.name || '—'} />
                <Info label={t('misc_sale.cashier')} value={sale.cashier?.name || '—'} />
                <Info label={t('misc_sale.items_count')} value={t('misc_sale.items_count_val', { count: items.length })} />
                <Info label={t('misc_sale.total_amount')} value={formatUZS(Number(sale.total_amount))} />
                {customer && <Info label={t('misc_sale.customer')} value={`${customer.full_name} · ${customer.phone}`} />}
                {murabahaCase && (
                  <Info
                    label={t('misc_sale.contract_id')}
                    value={
                      <Link to={`/murabaha/${murabahaCase.id}`} className="font-mono text-primary hover:underline break-all">
                        {murabahaCase.contract_number || t('misc_sale.pending')}
                      </Link>
                    }
                  />
                )}
              </div>
            </section>

            {/* To'lovlar */}
            <section className="bg-card border border-border rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3">{t('misc_sale.payments')}</h3>
              {payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('misc_sale.no_payments')}</p>
              ) : (
                <div className="divide-y divide-border">
                  {payments.map((p) => (
                    <div
                      key={p.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-3"
                    >
                      <span className="text-base text-foreground font-medium">{paymentLabel(p.method)}</span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(p.created_at).toLocaleString(dateLocale())}
                      </span>
                      <span className="text-base text-foreground font-semibold sm:text-right">
                        {formatUZS(Number(p.amount))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Mahsulotlar */}
            <section className="bg-card border border-border rounded-xl overflow-hidden">
              <h3 className="text-sm font-semibold text-foreground px-5 py-4 border-b border-border">
                {t('misc_sale.products_n', { count: items.length })}
              </h3>
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground p-5">{t('misc_sale.products_empty')}</p>
              ) : (
                <div className="divide-y divide-border">
                  {items.map((item) => (
                    <div key={item.id} className="p-5 space-y-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-base font-semibold text-foreground break-words">
                            {fieldValue(item, 'name')}
                          </p>
                          {!item.product && (
                            <span className="block text-xs text-muted-foreground mt-0.5">{t('misc_sale.not_in_catalog')}</span>
                          )}
                        </div>
                        <div className="md:text-right shrink-0">
                          <p className="text-sm text-muted-foreground">
                            {Number(item.quantity)} × {formatUZS(Number(item.unit_price))}
                          </p>
                          <p className="text-base font-semibold text-foreground">
                            {formatUZS(Number(item.total))}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                        {DETAIL_FIELDS.map((key) => (
                          <Field key={key} label={PRODUCT_FIELD_LABELS[key]} value={fieldValue(item, key)} />
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="flex items-center justify-between px-5 py-4 bg-muted/40">
                    <span className="text-sm font-medium text-muted-foreground">{t('misc_common.total')}</span>
                    <span className="text-base font-semibold text-foreground">{formatUZS(itemsTotal)}</span>
                  </div>
                </div>
              )}
            </section>

            {/* Qaytarishlar */}
            {refunds.length > 0 && (
              <section className="bg-card border border-border rounded-xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3">
                  {t('misc_sale.refunds_total', { amount: formatUZS(refundedTotal) })}
                </h3>
                <div className="divide-y divide-border">
                  {refunds.map((r) => (
                    <div key={r.id} className="py-3 space-y-0.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {r.refund_type === 'void' ? t('misc_sale.rf_void') : r.refund_type === 'partial' ? t('misc_sale.rf_partial') : t('misc_sale.rf_full')}
                        </span>
                        <span className="text-sm font-semibold text-foreground">{formatUZS(Number(r.amount))}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString(dateLocale())}
                        {r.reason ? ` · ${r.reason}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-between gap-2 shrink-0">
          {canRefund ? (
            <Button variant="destructive" onClick={() => setRefundOpen(true)}>
              <Undo2 size={16} className="mr-2" /> {t('misc_sale.refund_void_btn')}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => printReceipt({
                total: Number(sale.total_amount || 0),
                method: sale.payment_method,
                items: items.length,
                saleId: sale.id,
                cartItems: items.map((i) => ({
                  name: i.product_name,
                  quantity: Number(i.quantity),
                  total: Number(i.total),
                  mhik: i.mhik,
                  unit: i.unit,
                })),
                customerName: customer?.full_name ?? null,
                payments: payments.map((p) => ({ method: p.method, amount: Number(p.amount) })),
                createdAt: sale.created_at,
                isCopy: true,
                merchantName: merchant?.name,
                merchantAddress: merchant?.address,
                merchantLogo: merchant?.logo_url,
              })}
            >
              <Printer size={16} className="mr-2" /> {t('misc_sale.reprint')}
            </Button>
            <Button variant="outline" onClick={onClose}>{t('misc_common.close')}</Button>
          </div>
        </div>

        <RefundDialog
          sale={sale}
          open={refundOpen}
          onClose={() => setRefundOpen(false)}
          onDone={() => { refetchRefunds(); onClose(); }}
        />
      </DialogContent>
    </Dialog>
  );

}
